import React from 'react';
import { Post } from '../types';

interface SidebarProps {
  categories: string[];
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
  topPosts: Post[];
  onPostClick: (post: Post) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  categories,
  selectedCategory,
  onCategorySelect,
  topPosts,
  onPostClick
}) => {
  return (
    <aside className="lg:col-span-4 space-y-6 sticky top-24 h-fit" role="complementary">
      <section className="ui-shell p-7" aria-labelledby="sidebar-categories-title">
        <div className="flex items-center space-x-3 mb-6">
          <span className="text-cyan-accent text-xs font-bold uppercase tracking-[0.24em]">Browse</span>
          <div className="flex-1 h-px bg-gradient-to-r from-cyan-accent/40 to-transparent" />
        </div>
        <h3 id="sidebar-categories-title" className="text-xl font-bold text-starlight-mist font-serif tracking-tight mb-4">Dream Categories</h3>
        <nav className="flex flex-wrap gap-2.5" aria-label="Category List">
          {categories.map((cat) => (
            <a
              key={cat}
              href={cat === 'All' ? '/' : `/?category=${encodeURIComponent(cat)}`}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onCategorySelect(cat);
              }}
              aria-current={selectedCategory === cat ? 'page' : undefined}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-widest border rounded-full transition-all inline-block no-underline ${selectedCategory === cat
                ? 'bg-cyan-accent/90 text-deep-void border-cyan-accent shadow-[0_8px_22px_-14px_rgba(107,91,149,1)]'
                : 'bg-deep-void/60 text-slate-400 border-white/10 hover:border-cyan-accent/50 hover:text-cyan-accent'
                }`}
            >
              {cat}
            </a>
          ))}
        </nav>
      </section>

      <section className="ui-shell p-7" aria-labelledby="sidebar-top-posts-title">
        <h3 id="sidebar-top-posts-title" className="text-xl font-bold text-starlight-mist mb-7 font-serif tracking-tight flex items-center gap-3">
          <span className="text-cyan-accent text-xs font-bold uppercase tracking-[0.24em]">Now</span>
          Most Interpreted Dreams
        </h3>
        <div className="space-y-5">
          {topPosts.slice(0, 5).map((post, idx) => (
            <a
              key={post.id}
              href={`/post/${post.slug || post.id}`}
              className="group flex space-x-4 cursor-pointer block items-start"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onPostClick(post);
              }}
              aria-label={`Read ${post.title}`}
            >
              <span className="text-3xl font-black text-white/20 group-hover:text-cyan-accent/45 transition-colors w-9 shrink-0 -mt-2 font-serif italic" aria-hidden="true">
                {idx + 1}
              </span>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-slate-300 line-clamp-2 leading-tight group-hover:text-cyan-accent transition-colors font-serif text-balance">
                  {post.title}
                </h4>
                <div className="flex items-center mt-2.5 space-x-2">
                  <span className="text-[9px] text-gold-highlight font-bold uppercase tracking-widest border border-gold-highlight/30 px-2 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </aside>
  );
};

export default Sidebar;
